/**
 * shared/rbac/actions — lista canônica de ações da command palette.
 *
 * Cada entrada define:
 * - `id`       identificador único da ação (inglês snake_case)
 * - `label`    rótulo exibido ao usuário (pt-BR)
 * - `icon`     nome do ícone do design system
 * - `type`     "navigate" para rotas | "action" para ações rápidas
 * - `path`     rota alvo (apenas para type="navigate")
 * - `requiredAction` ação `canDo` exigida (apenas para type="action");
 *              ausente = qualquer autenticado pode executar
 * - `requiredRoute`  rota `canSeeRoute` exigida (apenas para type="navigate");
 *              ausente = qualquer autenticado pode navegar
 * - `group`    categoria de exibição na palette
 *
 * Referências: Req 7, Req 7.1..7.4 (navegação e ações rápidas).
 * ST-03: lista de ações canônicas extraída para este módulo (TASK-22 §ST-03).
 */

import type { RouteId } from "./rbac.js";

/** Categorias de agrupamento na palette (Req 7.3). */
export type PaletteGroup =
  | "Navegação"
  | "Administração"
  | "Plataforma"
  | "Ações"
  | "Contas"
  | "Oportunidades";

/** Ação de navegação (abre uma rota). */
export interface NavigateAction {
  readonly id: string;
  readonly type: "navigate";
  readonly label: string;
  readonly icon: string;
  readonly path: string;
  readonly group: PaletteGroup;
  /** Rota cujo acesso é testado via `canSeeRoute` (ausente = livre). */
  readonly requiredRoute?: RouteId;
}

/** Ação rápida (dispara comportamento, opcionalmente navega). */
export interface QuickAction {
  readonly id: string;
  readonly type: "action";
  readonly label: string;
  readonly icon: string;
  readonly path: string;
  readonly group: PaletteGroup;
  /** Ação cujo acesso é testado via `canDo` (ausente = livre para autenticados). */
  readonly requiredAction?: string;
}

export type PaletteAction = NavigateAction | QuickAction;

/**
 * Lista canônica de ações da palette.
 * Ordem define a precedência de exibição no estado inicial (sem query).
 *
 * Req 7.1 — busca de telas (type=navigate).
 * Req 7.2 — ações rápidas (type=action): nova oportunidade, nova conta, localizar conta.
 * Req 7.4 — filtrados por papel/contexto via `requiredRoute`/`requiredAction`.
 */
export const PALETTE_ACTIONS: readonly PaletteAction[] = [
  // ---- Navegação principal (Req 7.1) ----------------------------------------
  {
    id: "nav_dashboard",
    type: "navigate",
    label: "Visão geral",
    icon: "dashboard",
    path: "/",
    group: "Navegação",
    requiredRoute: "dashboard",
  },
  {
    id: "nav_pipeline",
    type: "navigate",
    label: "Pipeline",
    icon: "kanban",
    path: "/pipeline",
    group: "Navegação",
    requiredRoute: "pipeline",
  },
  {
    id: "nav_accounts",
    type: "navigate",
    label: "Contas",
    icon: "accounts",
    path: "/accounts",
    group: "Navegação",
    requiredRoute: "accounts",
  },
  {
    id: "nav_partners",
    type: "navigate",
    label: "Parcerias",
    icon: "briefcase",
    path: "/partners",
    group: "Navegação",
    requiredRoute: "partners",
  },
  {
    id: "nav_activities",
    type: "navigate",
    label: "Atividades",
    icon: "activities",
    path: "/activities",
    group: "Navegação",
    requiredRoute: "activities",
  },
  {
    id: "nav_inbox",
    type: "navigate",
    label: "Caixa de entrada",
    icon: "inbox",
    path: "/inbox",
    group: "Navegação",
    requiredRoute: "inbox",
  },
  {
    id: "nav_conversations",
    type: "navigate",
    label: "Atendimento",
    icon: "message",
    path: "/conversations",
    group: "Navegação",
    requiredRoute: "conversations",
  },
  {
    id: "nav_reports",
    type: "navigate",
    label: "Relatórios",
    icon: "reports",
    path: "/reports",
    group: "Navegação",
    requiredRoute: "reports",
  },
  {
    id: "nav_settings",
    type: "navigate",
    label: "Configurações",
    icon: "settings",
    path: "/settings",
    group: "Navegação",
    requiredRoute: "settings",
  },

  // ---- Administração (Req 7.4 — filtrado por papel) -------------------------
  {
    id: "nav_admin",
    type: "navigate",
    label: "Administração",
    icon: "shieldCheck",
    path: "/admin",
    group: "Administração",
    requiredRoute: "admin",
  },
  {
    id: "nav_queues",
    type: "navigate",
    label: "Filas",
    icon: "inbox",
    path: "/queues",
    group: "Administração",
    requiredRoute: "queues",
  },
  {
    id: "nav_channels",
    type: "navigate",
    label: "Canais",
    icon: "plug",
    path: "/channels",
    group: "Administração",
    requiredRoute: "channels",
  },
  {
    id: "nav_audit",
    type: "navigate",
    label: "Auditoria",
    icon: "shieldCheck",
    path: "/audit",
    group: "Administração",
    requiredRoute: "audit",
  },
  {
    id: "nav_import",
    type: "navigate",
    label: "Importar dados",
    icon: "layers",
    path: "/import",
    group: "Administração",
    requiredRoute: "import",
  },
  {
    id: "nav_digest",
    type: "navigate",
    label: "Digest",
    icon: "bell",
    path: "/digest",
    group: "Administração",
    requiredRoute: "digest",
  },

  // ---- Plataforma (Req 7.4 — filtrado por papel) ----------------------------
  {
    id: "nav_platform",
    type: "navigate",
    label: "Plataforma",
    icon: "layers",
    path: "/platform",
    group: "Plataforma",
    requiredRoute: "platform",
  },

  // ---- Ações rápidas (Req 7.2) ----------------------------------------------
  // Finding F10: ações de criação requerem papel com permissão de escrita.
  // viewer (somente leitura) não deve ver nem executar ações de criação.
  {
    id: "action_new_opportunity",
    type: "action",
    label: "Nova oportunidade",
    icon: "kanban",
    path: "/pipeline?action=new",
    group: "Ações",
    // Exige papel com permissão de criação (exclui viewer — Req 7.4, Finding F10)
    requiredAction: "create:opportunity",
  },
  {
    id: "action_new_account",
    type: "action",
    label: "Nova conta",
    icon: "accounts",
    path: "/accounts?action=new",
    group: "Ações",
    // Exige papel com permissão de criação (exclui viewer — Req 7.4, Finding F10)
    requiredAction: "create:account",
  },
  {
    // Busca/localização de conta: operação de leitura — livre para qualquer
    // autenticado. O filtro de contas/oportunidades depende do escopo de tenant
    // do repositório (RLS no backend), não de RBAC de ação no frontend.
    id: "action_find_account",
    type: "action",
    label: "Localizar conta",
    icon: "search",
    path: "/accounts",
    group: "Ações",
  },
] as const;
